import React, { useContext, useMemo, useState } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Grid,
  Typography,
  useTheme,
  Box,
  Button,
  styled,
} from "@mui/material";
import { UnfoldMore, ExpandLess, ExpandMore } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { AuthContext } from "../../context/auth-context/AuthContext";
import { doesPermissionsHaveSomeOf } from "../../utils/authUtils";
import useContactPersons from "../../hooks/contactPersons/useContactPersons";
import { ContactPersonDetailsDto, CrmContactStatus } from "../../types/companyTypes";
import ContactPreferenceChip from "../../components/contactpersons/ContactPreferenceChip";
import PotentialFollowUpProjectChip from "../../components/contactpersons/PotentialFollowUpProjectChip";

const StyledForm = styled("form")(({ theme }) => ({
  [theme.breakpoints.up("md")]: {
    display: "flex",
    alignItems: "flex-end",
  },
  [theme.breakpoints.down("xl")]: {
    display: "flex",
    alignItems: "center",
  },
}));

const StyledLink = styled(Link)`
  text-decoration: none;
  color: black;
  &:hover {
    text-decoration: underline;
  }
`;

type SortAttribute = "name" | "salutation" | "position" | "company";
type SortKey = `${SortAttribute}Asc` | `${SortAttribute}Desc` | "";

const sortComparators: Record<
  Exclude<SortKey, "">,
  (a: ContactPersonDetailsDto, b: ContactPersonDetailsDto) => number
> = {
  nameAsc: (a, b) => a.name.localeCompare(b.name),
  nameDesc: (a, b) => b.name.localeCompare(a.name),
  salutationAsc: (a, b) => a.salutation.localeCompare(b.salutation),
  salutationDesc: (a, b) => b.salutation.localeCompare(a.salutation),
  positionAsc: (a, b) => a.position.localeCompare(b.position),
  positionDesc: (a, b) => b.position.localeCompare(a.position),
  companyAsc: (a, b) => a.companyName.localeCompare(b.companyName),
  companyDesc: (a, b) => b.companyName.localeCompare(a.companyName),
};

const ContactPersonOverview: React.FunctionComponent = () => {
  const theme = useTheme();

  const styles = {
    amountOfEntries: {
      marginBottom: "10px",
      padding: "7px",
      textAlign: "center",
      fontWeight: "bold",
    },
    filterContainer: {
      marginBottom: "10px",
    },
    filterElement: {
      [theme.breakpoints.up("md")]: {
        margin: "7px",
        width: "155px",
      },
      [theme.breakpoints.down("xl")]: {
        margin: "7px",
        width: "120px",
      },
    },
    tableContainer: {
      maxHeight: "calc(100vh - 310px)",
    },
    tableHeadCell: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    },
    button: {
      marginTop: "20px",
      marginBottom: "20px",
      border: "0",
      backgroundColor: theme.palette.primary.main,
      color: "white",
      "&:hover": {
        color: "black",
      },
    },
  };

  const [searchFilter, setSearchFilter] = useState("");
  const [sortBy, setSortBy] = useState<SortKey>("");
  const { allContactPersons } = useContactPersons();
  const { auth } = useContext(AuthContext);
  const hasCrmPermissions = doesPermissionsHaveSomeOf(auth.permissions, [13]);

  const filteredAndSorted = useMemo(() => {
    const search = searchFilter.toLowerCase();
    const filtered = allContactPersons.filter(
      (contactPerson) =>
        contactPerson.name.toLowerCase().includes(search) ||
        contactPerson.companyName.toLowerCase().includes(search) ||
        contactPerson.position.toLowerCase().includes(search)
    );

    return sortBy ? [...filtered].sort(sortComparators[sortBy]) : filtered;
  }, [allContactPersons, searchFilter, sortBy]);

  const toggleSort = (attribute: SortAttribute) => {
    setSortBy((prev) => {
      if (prev === `${attribute}Asc`) return `${attribute}Desc`;
      if (prev === `${attribute}Desc`) return "";
      return `${attribute}Asc`;
    });
  };

  const getSortDirection = (attribute: SortAttribute): "asc" | "desc" => {
    if (sortBy === `${attribute}Desc`) {
      return "desc";
    }
    return "asc";
  };

  const isSortedBy = (attribute: SortAttribute) => sortBy === `${attribute}Asc` || sortBy === `${attribute}Desc`;

  return (
    <div>
      {hasCrmPermissions && (
        <Grid container spacing={3}>
          <Grid item>
            <Button sx={styles.button} variant="contained" component={Link} to="/ansprechpersonen/neu">
              Neue Ansprechperson anlegen
            </Button>
          </Grid>
        </Grid>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", marginBottom: "10px" }}>
        Du siehst nur Ansprechpersonen, bei denen du JBT-Kontakt bist oder PL eines zugeordneten Projekts.
      </Typography>
      <Paper sx={styles.filterContainer}>
        <StyledForm noValidate autoComplete="off">
          <Grid container spacing={1}>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Suche"
                sx={styles.filterElement}
                color="primary"
                onChange={(event) => setSearchFilter(event.target.value)}
              />
            </Grid>
          </Grid>
        </StyledForm>
        <Box sx={styles.amountOfEntries}>{`${filteredAndSorted.length} Einträge`}</Box>
      </Paper>
      <TableContainer component={Paper} sx={styles.tableContainer}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              <TableCell
                sx={styles.tableHeadCell}
                sortDirection={isSortedBy("name") ? getSortDirection("name") : false}
              >
                <TableSortLabel
                  active={isSortedBy("name")}
                  direction={getSortDirection("name")}
                  IconComponent={
                    isSortedBy("name") ? (getSortDirection("name") === "asc" ? ExpandMore : ExpandLess) : UnfoldMore
                  }
                  onClick={() => toggleSort("name")}
                  sx={{
                    color: "inherit",
                    "&.Mui-active": { color: "inherit" },
                    "& .MuiTableSortLabel-icon": { color: "inherit !important" },
                  }}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell
                sx={styles.tableHeadCell}
                sortDirection={isSortedBy("salutation") ? getSortDirection("salutation") : false}
              >
                <TableSortLabel
                  active={isSortedBy("salutation")}
                  direction={getSortDirection("salutation")}
                  IconComponent={
                    isSortedBy("salutation")
                      ? getSortDirection("salutation") === "asc"
                        ? ExpandMore
                        : ExpandLess
                      : UnfoldMore
                  }
                  onClick={() => toggleSort("salutation")}
                  sx={{
                    color: "inherit",
                    "&.Mui-active": { color: "inherit" },
                    "& .MuiTableSortLabel-icon": { color: "inherit !important" },
                  }}
                >
                  Anrede
                </TableSortLabel>
              </TableCell>
              <TableCell
                sx={styles.tableHeadCell}
                sortDirection={isSortedBy("position") ? getSortDirection("position") : false}
              >
                <TableSortLabel
                  active={isSortedBy("position")}
                  direction={getSortDirection("position")}
                  IconComponent={
                    isSortedBy("position")
                      ? getSortDirection("position") === "asc"
                        ? ExpandMore
                        : ExpandLess
                      : UnfoldMore
                  }
                  onClick={() => toggleSort("position")}
                  sx={{
                    color: "inherit",
                    "&.Mui-active": { color: "inherit" },
                    "& .MuiTableSortLabel-icon": { color: "inherit !important" },
                  }}
                >
                  Position
                </TableSortLabel>
              </TableCell>
              <TableCell
                sx={styles.tableHeadCell}
                sortDirection={isSortedBy("company") ? getSortDirection("company") : false}
              >
                <TableSortLabel
                  active={isSortedBy("company")}
                  direction={getSortDirection("company")}
                  IconComponent={
                    isSortedBy("company")
                      ? getSortDirection("company") === "asc"
                        ? ExpandMore
                        : ExpandLess
                      : UnfoldMore
                  }
                  onClick={() => toggleSort("company")}
                  sx={{
                    color: "inherit",
                    "&.Mui-active": { color: "inherit" },
                    "& .MuiTableSortLabel-icon": { color: "inherit !important" },
                  }}
                >
                  Unternehmen
                </TableSortLabel>
              </TableCell>
              <TableCell sx={styles.tableHeadCell}>Telefon Mobil</TableCell>
              <TableCell sx={styles.tableHeadCell}>Kontaktstatus</TableCell>
              <TableCell sx={styles.tableHeadCell}>JBT-Kontakt</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAndSorted.map((contactPerson) => (
              <TableRow hover key={contactPerson.contactPersonId}>
                <TableCell component="th" scope="row">
                  <Typography color="secondary">
                    <StyledLink to={`/ansprechpersonen/${contactPerson.contactPersonId}`}>
                      {contactPerson.name}
                    </StyledLink>
                  </Typography>
                </TableCell>
                <TableCell>{contactPerson.salutation}</TableCell>
                <TableCell>{contactPerson.position}</TableCell>
                <TableCell>
                  <Typography color="secondary">
                    <StyledLink to={`/unternehmen/${contactPerson.companyId}`}>{contactPerson.companyName}</StyledLink>
                  </Typography>
                </TableCell>
                <TableCell>{contactPerson.mobilePhone}</TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <ContactPreferenceChip
                      crmContactAllowed={contactPerson.crmContactAllowed}
                      crmContactStatus={contactPerson.crmContactStatus}
                    />
                    <PotentialFollowUpProjectChip
                      visible={contactPerson.crmContactStatus === CrmContactStatus.POTENTIAL_FUTURE_PROJECT}
                    />
                  </Box>
                </TableCell>
                <TableCell>
                  {contactPerson.jbtContactId ? (
                    <Typography color="secondary">
                      <StyledLink to={`/gesamtuebersicht/${contactPerson.jbtContactId}`}>
                        {contactPerson.jbtContactName}
                      </StyledLink>
                    </Typography>
                  ) : (
                    contactPerson.jbtContactName
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default ContactPersonOverview;
